import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { formatApiError } from '../lib/errors';
import { ArrowRight, Loader2, RefreshCw } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(formatApiError(error, 'Invalid credentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-slate-950">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-emerald-500/10" />
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233b82f6' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
        <div className="relative z-10 flex flex-col justify-center items-center text-center w-full px-12 xl:px-20">
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center">
              <RefreshCw className="w-7 h-7 text-white" />
            </div>
            <span className="text-2xl font-bold font-heading text-white tracking-tight">SyncPoint</span>
          </div>
          <h1 className="text-4xl xl:text-5xl font-bold font-heading text-white tracking-tight leading-tight mb-6 max-w-xl" data-testid="login-tagline">
            The bridge between your registers <span className="text-slate-500">and</span> your books
          </h1>
          <p className="text-slate-400 text-lg max-w-md mx-auto">
            Automatically post UnTill sales, customers, products and payments into the back office
            you already run — Odoo ERP or QuickBooks Online — with real-time posting, per-payment-method
            deposit mapping, monitoring and error recovery.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <span className="text-xs uppercase tracking-wider text-slate-500 mr-1">Connects</span>
            <span className="px-2.5 py-1 rounded-md bg-blue-500/10 border border-blue-500/30 text-blue-300 text-xs font-medium">
              UnTill POS
            </span>
            <span className="text-slate-600">→</span>
            <span className="px-2.5 py-1 rounded-md bg-purple-500/10 border border-purple-500/30 text-purple-300 text-xs font-medium">
              Odoo ERP
            </span>
            <span className="px-2.5 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-medium">
              QuickBooks Online
            </span>
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-8">
            <div>
              <div className="text-3xl font-bold text-white">99.9%</div>
              <div className="text-slate-500 text-sm">Sync Accuracy</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">&lt;30s</div>
              <div className="text-slate-500 text-sm">POS → Books Latency</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">24/7</div>
              <div className="text-slate-500 text-sm">Monitoring</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-8 sm:p-8 bg-slate-950">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8" data-testid="login-mobile-brand">
            <div className="flex items-center gap-3 mb-4 justify-center">
              <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold font-heading text-white">SyncPoint</span>
            </div>
            <p className="text-2xl sm:text-3xl font-bold font-heading text-white tracking-tight leading-tight max-w-sm sm:max-w-md mx-auto">
              The bridge between your registers <span className="text-slate-500">and</span> your books
            </p>
          </div>

          <Card className="border-slate-800 bg-slate-900/50">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="text-2xl font-heading font-bold text-white">Welcome back</CardTitle>
              <CardDescription className="text-slate-400">
                Sign in to access your sync dashboard
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@syncpoint.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20"
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-slate-300">Password</Label>
                    <Link to="/forgot-password" className="text-xs text-blue-400 hover:text-blue-300" data-testid="forgot-password-link">
                      Forgot password?
                    </Link>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20"
                    data-testid="login-password-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium h-11 transition-colors"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <ArrowRight className="w-4 h-4 mr-2" />
                  )}
                  Sign In
                </Button>
              </form>
              <div className="mt-6 text-center">
                <p className="text-slate-400 text-sm">
                  Don't have an account?{' '}
                  <Link to="/register" className="text-blue-400 hover:text-blue-300 font-medium transition-colors">
                    Create one
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
